/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import 'whatwg-fetch';

import p from 'polyglot/polyglot';

const CLOUDINARY_PRESET = 'h2lkq62l';
const CLOUDINARY_UPLOAD_URL =
  'https://api.cloudinary.com/v1_1/ksting/image/upload';

/**
 * Creates request object and returns a fetch promise.
 *
 * @param {Object} data - Picture data object.
 * @param {string} data.key - UUID.
 * @param {blob} data.file - Image file.
 *
 * @return {Promise}
 */
function callCloudinary(data) {
  const { fetch } = window;

  const request = {
    method: 'POST',
  };

  if (!data || typeof data !== 'object') {
    return new Promise((resolve, reject) =>
      reject({
        message: 'Request must contain a data object.',
      })
    );
  }

  const body = new FormData();
  const { pathname } = window.location;

  body.append('success_action_status', '200');

  if (data.file && data.key) {
    body.append('upload_preset', CLOUDINARY_PRESET);
    body.append('content-type', data.contentType);
    body.append('file', data.file, data.key);
  }

  if (data.context) {
    // always add the pathname to the context.
    data.context.push(`pathname=${pathname}`);

    body.append('context', data.context.join('|'));
  }

  // if file is bigger then 10MB abort.
  if (data.file.size > 10000000) {
    return new Promise((resolve, reject) =>
      reject({
        message: p.t('portfolio.messages.file_too_big'),
      })
    );
  }

  request.body = body;

  return fetch(CLOUDINARY_UPLOAD_URL, request)
    .then(response => {
      if (!response.ok) {
        throw new Error('Something went wrong.');
      }

      return response.json();
    })
    .then(cloudinaryData => {
      const { public_id, secure_url } = cloudinaryData;

      return { key: public_id, url: secure_url };
    })
    .catch(err => Promise.reject(err));
}

/**
 * Cloudinary symbol.
 *
 * @type {Symbol}
 */
export const CLOUDINARY = Symbol('CLOUDINARY');

/**
 * Cloudinary middleware, wraps action with fetch request. Subsequently
 * triggers actions corresponding to the types passed with the initial action.
 *
 * @return {Promise}
 */
export default () => next => action => {
  const actionData = action[CLOUDINARY];

  // Prevent middleware from going past this point if not CLOUDINARY is specified.
  if (typeof actionData === 'undefined') {
    return next(action);
  }

  const { body } = actionData;
  const [requestType, successType, failureType] = actionData.types;

  next({ type: requestType });

  return callCloudinary(body).then(
    response =>
      next({
        type: successType,
        payload: response,
      }),
    errors =>
      next({
        type: failureType,
        payload: errors || 'There was an error.',
      })
  );
};
